import { useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiGmnMeasuringpointsListRequest, Measuringpoint } from "@nens/brostar/openapi-client";

import { getApi } from "@/api/api-configuration";

import { fetchAllPages } from "./helpers/fetch-all-pages";
import { findInListQuery } from "./helpers/find-in-list-query";
import { queryDirectly } from "./helpers/query-directly";
import { simplifyQueryParam } from "./helpers/simplify-query-key";

const uploadApi = getApi();

export type GmnMeasuringPointNoStart = Omit<Measuringpoint, "eventType"> & {
  eventType: Exclude<Measuringpoint["eventType"], "GMN_StartRegistration"> | null;
};

type GmnMeasuringPointRequestWithGmn = Omit<ApiGmnMeasuringpointsListRequest, "gmn"> & {
  gmn: string;
};

// ─── SINGLE ──────────────────────────────────────────────────────────────────

const getGmnMeasuringPointSingleQuery = (uuid: string) => ({
  queryKey: ["gmn-measuringpoint-single", uuid],
  queryFn: async () => await uploadApi.apiGmnMeasuringpointsRead({ uuid }),
});

export const useGmnMeasuringPointSingleQueryDirectly = () => {
  const queryClient = useQueryClient();
  return async (uuid: string) => {
    const result = await findInListQuery(
      queryClient,
      ["gmn-measuringpoint-all"],
      uuid,
      getGmnMeasuringPointSingleQuery(uuid)
    );
    // Single measuring points with eventType GMN_StartRegistration are not allowed,
    // because those are only used in the context of a start registration and
    // should not be fetched individually. So we throw a warning if we encounter one of those.
    if (result.eventType === "GMN_StartRegistration")
      console.warn(
        "Fetched a GMN measuring point with eventType GMN_StartRegistration in single query, this should not happen"
      );
    return result as GmnMeasuringPointNoStart;
  };
};

// ─── LIST ─────────────────────────────────────────────────────────────────────

const getGmnMeasuringPointListAllPagesQuery = ({
  gmn,
  ...options
}: GmnMeasuringPointRequestWithGmn) => ({
  queryKey: ["gmn-measuringpoint-all", gmn, simplifyQueryParam(options)],
  queryFn: () =>
    fetchAllPages((...pageOptions) =>
      uploadApi.apiGmnMeasuringpointsList({ gmn, ...options, ...pageOptions })
    ),
});

// FUTURE: Implement a paginated query for measuring points. Some GMN's can have thousands of measuring points so that doesnt work correctly
export const useGmnMeasuringPointListAllPagesQuery = (options: GmnMeasuringPointRequestWithGmn) => {
  return useQuery(getGmnMeasuringPointListAllPagesQuery(options));
};

export const useGmnMeasuringPointListAllPagesQueryDirectly = () => {
  const queryClient = useQueryClient();
  return (options: GmnMeasuringPointRequestWithGmn) =>
    queryDirectly(queryClient, getGmnMeasuringPointListAllPagesQuery(options));
};

// ─── INVALIDATION ─────────────────────────────────────────────────────────────

export const useInvalidateGmnMeasuringPointQueries = () => {
  const queryClient = useQueryClient();
  return async (gmn: string) => {
    await queryClient.invalidateQueries({
      queryKey: ["gmn-measuringpoint-single"],
      refetchType: "all",
    });
    await queryClient.invalidateQueries({
      queryKey: getGmnMeasuringPointListAllPagesQuery({ gmn }).queryKey.slice(0, -1),
      refetchType: "all",
    });
  };
};
